import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation } from 'react-router-dom'; 
import './CalendarDetails.css';
import { format, isSameMonth, isSameDay, parseISO } from 'date-fns';
import Calendar from '../Calendar/Calendar';
import convenienceApi from '../../common/api/convenience';
import EventCard from '../EventCard/EventCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MonitoredIndividualSummaryBar from '../MonitoredIndividualSummaryBar/MonitoredIndividualSummaryBar';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';

export default function CalendarDetails() {
  const environmentContext = useContext(EnvironmentContext);
  const location = useLocation();
  const [visibleDate, setVisibleDate] = useState(location.state ? location.state.visibleDate : new Date());
  const [facilityEvents, setFacilityEvents] = useState(null);
  const [monitoredIndividualEvents, setMonitoredIndividualEvents] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(true);
  const [facilityEventsForVisibleDate, setFacilityEventsForVisibleDate] = useState(null);
  const [monitoredIndividualEventsForVisibleDate, setMonitoredIndividualEventsForVisibleDate] = useState(null);
  const calendarRef = useRef(null);
  const calendarWrapperRef = useRef(null);
  const calendarDetailsRef = useRef(null);
  const [needFloatCalendar, setNeedFloatCalendar] = useState(false);
  const { activeUser: user, selectedMonitoredIndividual } = environmentContext;
  const visibleDateRef = useRef(location.state.selectedDate);
  const facilityEventsRef = useRef(null);
  const monitoredIndividualEventsRef = useRef(null);


  const setVisibleDateRef = (el) => {
    if (logComponent('UserCreationModal')) { console.debug('CalendarDetails setVisibleDateRef'); }
    visibleDateRef.current = location.state.selectedDate;
    let monthChanged = !isSameMonth(el,visibleDateRef.current)
    visibleDateRef.current = el;
    setVisibleDate(el);
    if (monthChanged)
      loadData();

  }

  const setFacilityEventsRef = (el) => {
    if (logComponent('UserCreationModal')) { console.debug('CalendarDetails setFacilityEventsRef'); }
    facilityEventsRef.current = el;
    setFacilityEvents(el);

  }


  const setMonitoredIndividualEventsRef = (el) => {
    if (logComponent('UserCreationModal')) { console.debug('CalendarDetails setMonitoredIndividualEventsRef'); }
    monitoredIndividualEventsRef.current = el;
    setMonitoredIndividualEvents(el);

  }

  if (logComponent('CalendarDetails')) { console.debug('CalendarDetails rendered'); }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails onMount'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;
    window.scrollTo(0, 0);

    loadData();
    window.addEventListener('scroll', floatCalendar);
    window.addEventListener('resize', floatCalendar);
    return function cleanup() {
      window.removeEventListener('scroll', floatCalendar);
      window.removeEventListener('resize', floatCalendar);
    };
  }, []);


  useEffect(() => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails useEffect [environmentContext.selectedMonitoredIndividual]'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;

    // TODO: If MI facilityId is unchanged, don't reload facility events for month, only fetch MI registered events
    loadData();


  }, [environmentContext.selectedMonitoredIndividual]);

  useEffect(() => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails useEffect [environmentContext.hasToken]'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;

    window.scrollTo(0, 0);
    loadData();
  }, [environmentContext.hasToken]);


  useEffect(() => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails useEffect [visibleDate, facilityEvents, monitoredIndividualEvents ]'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;
    filterEventsByDate();


  }, [visibleDate, facilityEvents, monitoredIndividualEvents ]);


  useEffect(() => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails useEffect [needFloatCalendar]'); }
    if(needFloatCalendar === true) floatCalendar();


  }, [needFloatCalendar]);


  const filterEventsByDate = () => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails filterEventsByDate'); }
    if (!visibleDateRef.current) return;
    var facilityEventsForThisdate = facilityEventsRef.current ? facilityEventsRef.current.filter(event => isSameDay(parseISO(event.localStartOn), visibleDateRef.current)) : null;
    setFacilityEventsForVisibleDate(facilityEventsForThisdate);

    setMonitoredIndividualEventsForVisibleDate(monitoredIndividualEventsRef.current ? monitoredIndividualEventsRef.current.filter(event => isSameDay(parseISO(event.localStartOn), visibleDateRef.current)) : null);
    setNeedFloatCalendar(true);
  }

  const setCalendarRef = (el) => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails setCalendarRef'); }
    calendarRef.current = el;
  }

  const setCalendarWrapperRef = (el) => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails setCalendarWrapperRef'); }
    calendarWrapperRef.current = el;
  }

  const setCalendarDetailsRef = (el) => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails setCalendarDetailsRef'); }
    calendarDetailsRef.current = el;
  }

  const handleSetVisibleMonth = (date) => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails handleSetVisibleMonth'); }
    setVisibleDateRef(date);
  }

  const handleSetVisibleDate = (date) => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails handleSetVisibleDate'); }
   setVisibleDateRef(date);
  }

  const loadData = () => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails loadData'); }
    const { activeUser: user, selectedMonitoredIndividual, hasToken } = environmentContext;

    if (!hasToken) return;

    setFacilityEvents(null);
    setMonitoredIndividualEvents(null);
    setDataLoading(true);
    setDataError(false);


    convenienceApi.getCalendarDetailsData(user, selectedMonitoredIndividual, visibleDateRef.current)
      .then(
        result => {
          setFacilityEventsRef(result.facilityEvents);
          setMonitoredIndividualEventsRef(result.monitoredIndividualEvents);
          setDataLoading(false);
          setDataError(false);

        },
        error => {
          setDataLoading(false);
          setDataError(true);
        }
      )
  }

  const floatCalendar = () => {
    if (logComponent('CalendarDetails')) { console.debug('CalendarDetails floatCalendar'); }
    const calendarRect = calendarRef.current.getBoundingClientRect();
    const calendarWrapperRect = calendarWrapperRef.current.getBoundingClientRect();
    const calendarDetailsRect = calendarDetailsRef.current.getBoundingClientRect();

    let distanceFromCalendarToHeader = calendarWrapperRect.top - calendarDetailsRect.top + 32;

    let newOffset = Math.max(0, window.scrollY - distanceFromCalendarToHeader);

    const floatMax = calendarWrapperRect.height - calendarRect.height;

    let superNewOffset = Math.min(newOffset, floatMax);

    calendarRef.current.style.top = superNewOffset + 'px';
  }  



  return (
    <div className="Calendar-Details" ref={setCalendarDetailsRef}>
    {
      user.groupTypeString === 'Facility' &&
      selectedMonitoredIndividual &&
      
        <div className="Calendar-Details__monitoredindividual-summary-bar">
        <MonitoredIndividualSummaryBar
          monitoredIndividual={selectedMonitoredIndividual} />
      </div>
      }
      <div className="Calendar-Details__main">
        <p className="Calendar-Details__heading">Event calendar</p>
        <div className="Calendar-Details__body">
          <div className="Calendar-Details__calendar-wrapper" ref={setCalendarWrapperRef}>
            <div className="Calendar-Details__calendar" ref={setCalendarRef}>
              <Calendar 
                events={monitoredIndividualEventsRef.current} 
                eventsLoading={dataLoading}
                eventsError={dataError}
                reload={loadData}
                visibleMonth={visibleDateRef.current} 
                selectedDate={visibleDateRef.current}
                onDateClick={handleSetVisibleDate}
                setVisibleMonth={handleSetVisibleMonth} />
              <ul className="Calendar-Details__legend">
                <li key="today" className="Calendar-Details__legend-item">
                  <div className="Calendar-Details__today-icon"></div>
                  <p>Today</p>
                </li> 
              {
                !!selectedMonitoredIndividual &&
                <li key="one" className="Calendar-Details__legend-item">
                  <div className="Calendar-Details__has-event-icon"></div>
                  <p>Indicates at least one registration this day</p>
                </li> 
              }
                <li key="selected" className="Calendar-Details__legend-item">
                  <div className="Calendar-Details__selected-day-icon"></div>
                  <p>Selected day</p>
                </li>                                 
              </ul>
            </div>
          </div>
          <div className="Calendar-Details__daily-events">
          {
            facilityEventsForVisibleDate ?
            <React.Fragment>
                  <p className="Calendar-Details__daily-events-heading">{format(visibleDateRef.current, 'MMMM d, yyyy') }</p>
          {
            facilityEventsForVisibleDate.length === 0 ?
            <p className="Calendar-Details__empty-message">
              No events scheduled for this date.
            </p> :
            <ul className="Calendar-Details__daily-event-list">
            {
              facilityEventsForVisibleDate
                .map(event => 
                <li 
                  className="Calendar-Details__daily-event-item"
                  key={event.socialEventId}>
                  <EventCard event={event} />
                  {
                    monitoredIndividualEventsForVisibleDate.some(miEvent => miEvent.socialEventId === event.socialEventId) &&
                    <div className="Calendar-Details__user-registration-indicator">
                      <FontAwesomeIcon className="Calendar-Details__user-registration-icon" icon="check-circle" />
                      REGISTERED
                    </div>
                      
                  }
                </li>
              )
            }
            </ul>
          }
            </React.Fragment> :
            <p className="Calendar-Details__loading-message">Loading...</p>
          }
          </div>
        </div>          
      </div>
    </div>
  );
}